import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  PageHeader,
  Breadcrumbs,
  Avatar,
  Badge,
  Button,
  Tabs,
  Alert,
  Modal,
} from '@opendate-app/setlist';

import CrmPageLayout from '../../shared/components/CrmPageLayout';
import GrantFormModal from '../../Grants/components/GrantFormModal';
import GrantOverviewTab from './GrantOverviewTab';
import GrantKeyDatesTab from './GrantKeyDatesTab';
import GrantDocumentsTab from './GrantDocumentsTab';
import { GRANT_STAGE_CONFIG, grantStageConfig } from '../../Grants/grantStages';
import { initialsFor } from '../../Deals/components/dealStages';
import { organizationLabel } from '../grantLabels';
import { REFRESH_ERROR_COPY } from '../refreshError';

const SAVE_ERROR_COPY = "Couldn't save the change. Try again.";
const TAB_NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
const STEPPER_STAGES = ['prospect', 'drafting', 'submitted', 'awarded', 'complete'];
const DECLINABLE_STAGES = ['prospect', 'drafting', 'submitted'];

const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-xl)' };
const titleRow = { display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--setlist-spacing-md)' };
const actionsRow = { display: 'flex', gap: 'var(--setlist-spacing-lg)' };

export const errorMessage = (error) => {
  if (error && error.displayMessage) return error.displayMessage;
  const data = error && error.response && error.response.data;
  const messages = Array.isArray(data) ? data : (data && data.errors);
  if (Array.isArray(messages) && messages.length > 0) return messages.join(', ');
  return SAVE_ERROR_COPY;
};

const openKeyDatesCount = (milestones) => (milestones || [])
  .filter((milestone) => !milestone.done && milestone.key !== 'submission')
  .length;

const countOrNothing = (count) => (count > 0 ? count : undefined);

const enabledTabs = (root) => Array.from(root.querySelectorAll('[role="tab"]')).filter((tab) => !tab.disabled);

const focusTarget = (tabs, current, key) => {
  if (key === 'Home') return tabs[0];
  if (key === 'End') return tabs[tabs.length - 1];
  const index = tabs.indexOf(current);
  const step = key === 'ArrowRight' ? 1 : -1;
  if (index < 0) return tabs[0];
  return tabs[(index + step + tabs.length) % tabs.length];
};

const moveStepperFocus = (event) => {
  if (event.target.getAttribute('role') !== 'tab') return;
  if (!TAB_NAVIGATION_KEYS.includes(event.key)) return;
  event.preventDefault();
  event.stopPropagation();
  const tabs = enabledTabs(event.currentTarget);
  if (tabs.length === 0) return;
  focusTarget(tabs, document.activeElement, event.key).focus();
};

function GrantDetailPage({
  team,
  csrfToken,
  grant: initialGrant,
  owners = [],
  currentOwnerId = null,
  stages = [],
  funders = [],
  canManage = false,
}) {
  const [grant, setGrant] = useState(initialGrant);
  const [activeTab, setActiveTab] = useState('overview');
  const [documentsCount, setDocumentsCount] = useState(0);
  const [error, setError] = useState(null);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [editState, setEditState] = useState({ open: false, addReportingDeadline: false });

  const grantUrl = `/teams/${team.id}/crm/grants/${grant.id}.json`;
  const latestRefresh = useRef(0);
  const stageSaving = useRef(false);
  const stepperRef = useRef(null);

  useEffect(() => {
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
  }, [csrfToken]);

  useEffect(() => {
    const root = stepperRef.current;
    if (!root) return;
    const tabs = enabledTabs(root);
    const selected = root.querySelector('[role="tab"][aria-selected="true"]');
    if (selected) {
      tabs.filter((tab) => tab !== selected).forEach((tab) => { tab.tabIndex = -1; });
    } else if (tabs.length > 0) {
      tabs[0].tabIndex = 0;
    }
  });

  const refreshGrant = useCallback(async () => {
    latestRefresh.current += 1;
    const request = latestRefresh.current;
    const response = await axios.get(grantUrl);
    if (request === latestRefresh.current) setGrant(response.data);
    return response.data;
  }, [grantUrl]);

  const showError = useCallback((err) => setError(errorMessage(err)), []);

  const patchGrant = useCallback(async (attrs) => {
    try {
      setError(null);
      await axios.patch(grantUrl, { grant: attrs });
    } catch (err) {
      showError(err);
      return false;
    }
    try {
      await refreshGrant();
    } catch (err) {
      setError(REFRESH_ERROR_COPY);
    }
    return true;
  }, [grantUrl, refreshGrant, showError]);

  const openEdit = useCallback(({ addReportingDeadline = false } = {}) => {
    setEditState({ open: true, addReportingDeadline });
  }, []);

  const closeEdit = () => setEditState({ open: false, addReportingDeadline: false });

  const changeStage = async (stage) => {
    if (stage === grant.stage || stageSaving.current) return;
    stageSaving.current = true;
    await patchGrant({ stage });
    stageSaving.current = false;
  };

  const confirmDecline = () => {
    setConfirmingDecline(false);
    changeStage('declined');
  };

  const stageConfig = grantStageConfig(grant.stage);
  const supportingText = [grant.funder, organizationLabel(grant)].filter(Boolean).join(' · ') || undefined;

  const actions = canManage ? (
    <div style={actionsRow}>
      {DECLINABLE_STAGES.includes(grant.stage) && (
        <Button variant="secondary" onClick={() => setConfirmingDecline(true)}>Mark declined</Button>
      )}
      <Button variant="primary" onClick={() => openEdit()}>Edit grant</Button>
    </div>
  ) : undefined;

  const stepperItems = STEPPER_STAGES.map((stage) => ({
    value: stage,
    label: GRANT_STAGE_CONFIG[stage].label,
    disabled: !canManage,
  }));

  const sectionItems = [
    { value: 'overview', label: 'Overview' },
    { value: 'dates', label: 'Key dates', count: countOrNothing(openKeyDatesCount(grant.milestones)) },
    { value: 'documents', label: 'Documents', count: countOrNothing(documentsCount) },
  ];

  const tabProps = {
    team,
    csrfToken,
    grant,
    owners,
    canManage,
    refreshGrant,
    patchGrant,
    openEdit,
    onError: showError,
    onShowDocuments: () => setActiveTab('documents'),
    documentsCount,
    onDocumentsChange: setDocumentsCount,
  };

  return (
    <CrmPageLayout team={team} activeSidebarKey="grants">
      <div style={stack}>
        <PageHeader
          breadcrumbs={(
            <Breadcrumbs
              items={[
                { label: 'CRM', href: `/teams/${team.id}/crm_dashboard` },
                { label: 'Grants', href: `/teams/${team.id}/crm/grants` },
                { label: grant.name },
              ]}
            />
          )}
          avatar={<Avatar initials={initialsFor(grant.name)} />}
          title={(
            <span style={titleRow}>
              {grant.name}
              <Badge color={stageConfig.color} dot>{stageConfig.label}</Badge>
            </span>
          )}
          supportingText={supportingText}
          actions={actions}
        />

        {error && (
          <Alert color="error" role="alert" title={error} onDismiss={() => setError(null)} />
        )}

        <div ref={stepperRef}>
          <Tabs
            aria-label="Grant status"
            variant="button-border"
            size="sm"
            items={stepperItems}
            value={STEPPER_STAGES.includes(grant.stage) ? grant.stage : null}
            onChange={changeStage}
            onKeyDownCapture={moveStepperFocus}
          />
        </div>

        <Tabs
          aria-label="Grant sections"
          items={sectionItems}
          value={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === 'overview' && <GrantOverviewTab {...tabProps} />}
        {activeTab === 'dates' && <GrantKeyDatesTab {...tabProps} />}
        {activeTab === 'documents' && <GrantDocumentsTab {...tabProps} />}
      </div>

      <Modal
        open={confirmingDecline}
        onClose={() => setConfirmingDecline(false)}
        type="error"
        title="Mark this grant declined?"
        description="The grant moves to Declined. You can move it back with the status stepper."
        cancelLabel="Cancel"
        confirmLabel="Mark declined"
        onConfirm={confirmDecline}
      />

      <GrantFormModal
        isOpen={editState.open}
        onClose={closeEdit}
        grant={grant}
        team={team}
        csrfToken={csrfToken}
        owners={owners}
        currentOwnerId={currentOwnerId}
        stages={stages}
        funders={funders}
        addReportingDeadline={editState.addReportingDeadline}
        onSaved={() => refreshGrant().catch(() => setError(REFRESH_ERROR_COPY))}
      />
    </CrmPageLayout>
  );
}

GrantDetailPage.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number }).isRequired,
  csrfToken: PropTypes.string,
  currentUser: PropTypes.object,
  grant: PropTypes.object.isRequired,
  owners: PropTypes.array,
  currentOwnerId: PropTypes.number,
  stages: PropTypes.array,
  funders: PropTypes.array,
  canManage: PropTypes.bool,
};

export default GrantDetailPage;
